import React, { useCallback, useEffect, useMemo } from 'react';
import {
  HistoryAction,
  HistoryType,
  PublicationSearchDocument,
  PublicationSectionSearchEdge,
} from '__generated__/graphql';
import { PublicationResult } from 'pages/secondary-sources/PublicationResult';
import { TypedSearchResults } from '../TypedSearchResults';
import { EmptyResults } from '../EmptyResults';
import styled from 'styled-components';
import TitledPage from 'components/common/TitledPage';
import { SearchFilters, StyledSearchWithin } from '../SearchFilters';
import {
  StyledFormCheckbox,
  StyledSearchCheckboxes,
  StyledSearchResults,
  StyledSearchHeader,
  StyledSearchContent,
} from '../SearchStyles';
import { FilterChips } from '../FilterChips';
import { QueryInput } from 'components/common/query-input';
import { SecondarySourcesFilters } from './SecondarySourcesFilters';
import { ShowInPlanRocker } from 'components/secondary-sources/ShowInPlanRocker';
import useSearchVariables from '../useSearchVariables';

import usePublicationOptionFilters from './usePublicationOptionFilters';
import { SearchWithin } from '../SearchWithin';
import { useAssetAccess } from 'components/asset-access';
import { TrackHistory } from 'hooks/useHistory';

export function SecondarySourcesSearch() {
  const outofplanOptionFilters = usePublicationOptionFilters();
  const mutateVariables: NonNullable<Parameters<typeof useSearchVariables>[0]> =
    useCallback(
      (variables, _queryString) => ({
        ...variables,
        optionFilters: outofplanOptionFilters,
      }),
      [outofplanOptionFilters],
    );
  const variables = useSearchVariables(mutateVariables);
  const { hasSecondarySourcesAllAccess } = useAssetAccess();

  return (
    <TitledPage title="Search Secondary Sources">
      <TrackHistory
        text={variables.query}
        type={HistoryType.SecondarySources}
        action={HistoryAction.Search}
        subtype="SEARCH_SECONDARY_SOURCES"
      />

      <SearchFilters queryVariables={['textFilters', 'optionFilters']}>
        <SecondarySourcesFilters
          variables={variables}
          optionFilters={variables.optionFilters}
        />
      </SearchFilters>

      <StyledSearchContent>
        <StyledSearchHeader>
          <div>
            <StyledSearchWithin>{<SearchWithin />}</StyledSearchWithin>
            <StyledSearchCheckboxes>
              <QueryInput
                through={StyledFormsOnlyCheckbox}
                type="checkbox"
                name="form_true"
                value="yes"
                placeholder="Show forms only"
              />
              {!hasSecondarySourcesAllAccess && (
                <div>
                  <StyledPlanRocker />
                </div>
              )}
            </StyledSearchCheckboxes>
          </div>
          <div>
            <FilterChips
              filters={{
                'Practice Area': 'practicearea_',
                Titles: 'title_',
                'Resource Types': 'resourcetype_',
              }}
            />
          </div>
        </StyledSearchHeader>
        <StyledSecondarySourcesSearchResults hasHeader>
          <TypedSearchResults
            resultTitle="Secondary Sources Publication Result"
            query={PublicationSearchDocument}
            emptyResults={<EmptyResults />}
            variables={variables}
          >
            {result => (
              <StyledPublicationResult
                key={(result as PublicationSectionSearchEdge).node.id}
                result={result as PublicationSectionSearchEdge}
              />
            )}
          </TypedSearchResults>
        </StyledSecondarySourcesSearchResults>
      </StyledSearchContent>
    </TitledPage>
  );
}

const StyledSecondarySourcesSearchResults = styled(StyledSearchResults)`
  padding: 27px 75px 48px;
`;

const StyledFormsOnlyCheckbox = styled(StyledFormCheckbox)`
  padding: 0;
`;

const StyledPlanRocker = styled(ShowInPlanRocker)`
  color: var(--COLOR_COOL_GRAY);
  border-radius: 5px;
  border-color: color-mix(in srgb, var(--COLOR_COOL_GRAY) 45%, transparent);
  margin: 0 -14px 0 0;
  label {
    input:checked + span {
      background: var(--COLOR_COOL_GRAY);
    }
    > span {
      box-sizing: border-box;
      height: 100%;
      padding: 6px 3px 4px;
      min-width: 98px;
    }
  }
`;

const StyledPublicationResult = styled(PublicationResult)`
  padding: 0;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 36px;
`;

StyledPublicationResult.displayName = 'StyledPublicationResult';
